/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023, 2024.
 */

import { Linkable } from '@/components/blocks/Linkable';
import { Img } from '@/components/blocks/MaterialImage';
import {
	headerMiniCartButtonSX,
	headerMiniCartTermsLinkSX,
	headerMiniCartTermsTextSX,
} from '@/components/content/Header/styles/miniCart/button';
import {
	headerMiniCartContainerSX,
	headerMiniCartImageSX,
} from '@/components/content/Header/styles/miniCart/emptyView';
import {
	headerMiniCartItemSX,
	headerMiniCartLabelItemSX,
} from '@/components/content/Header/styles/miniCart/item';
import { headerMiniCartMenuCustomSX } from '@/components/content/Header/styles/miniCart/menu';
import {
	headerNavBarDropMenuStickyBarSX,
	headerNavBarDropMenuStickyContainerSX,
	headerNavBarDropMenuStickySubtotalSX,
} from '@/components/content/Header/styles/navBar/dropMenu';
import { OrderItemTable } from '@/components/content/OrderItemTable';
import { useCart } from '@/data/Content/Cart';
import { useCurrencyFormat } from '@/data/Content/CurrencyFormat';
import { useLocalization } from '@/data/Localization';
import { useSettings } from '@/data/Settings';
import { logEvent } from '@/data/utils/logEventsUtils';
import { formatPrice } from '@/utils/formatPrice';
import { Divider, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { FC } from 'react';
import { CDN_BASE_URL, LOCALE_ES_MX } from 'shared/custom/constants/commonConstants';

export const HeaderMiniCartDropMenu: FC<{ drawerView?: boolean }> = ({ drawerView = false }) => {
	const theme = useTheme();
	const { settings } = useSettings();
	const { decimalPlaces } = useCurrencyFormat();
	const CartLabels = useLocalization('MiniCart');
	const RouteLabels = useLocalization('Routes');
	const { orderItems, data: order, count, isFetchingCart, loading } = useCart();
	const totalPrice = order?.grandTotal ?? '0.00';
	const currency = order?.grandTotalCurrency ?? settings.defaultCurrency;
	const imageEmptyCartUrl = '/assets/img/cart.svg';
	const isMobileApp = settings.isMobileApp;

	const onHandleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
		if (isMobileApp) {
			e.preventDefault();
			logEvent('goToCart');
		}
	};

	if (loading)
		return (
			<Stack sx={headerMiniCartMenuCustomSX(theme)} id="quick-panel-modal">
				{!drawerView ? (
					<>
						<Typography variant="headingSegment" sx={headerMiniCartItemSX}>
							{CartLabels.Title.t()}
						</Typography>
						<Divider />
					</>
				) : null}

				<Stack sx={{ pt: '15px', pl: 2, pr: 2, pb: 2 }} spacing={1}>
					<Skeleton variant="rectangular" height={80} />
					<Skeleton variant="rectangular" height={80} />
					<Skeleton variant="rectangular" height={40} />
				</Stack>
			</Stack>
		);

	return (
		<Stack sx={headerMiniCartMenuCustomSX(theme)} id="quick-panel-modal">
			{!drawerView ? (
				<>
					<Typography variant="headingSegment" sx={headerMiniCartItemSX}>
						{CartLabels.Title.t()}
					</Typography>
					<Divider />
				</>
			) : null}
			{orderItems?.length > 0 ? (
				<Stack sx={{ pt: '15px' }}>
					<OrderItemTable orderItems={orderItems} orderId={order?.orderId} variant="mini" />
					<Stack direction="column" sx={headerNavBarDropMenuStickyBarSX}>
						<Divider sx={{ pb: '10px' }} />
						<Stack direction="column" sx={headerNavBarDropMenuStickyContainerSX}>
							<Stack direction="row" justifyContent="space-between">
								<Stack direction="row" spacing={1}>
									<Typography
										variant="bodyBaseline"
										sx={{ color: 'grey.200' }}
										className="quick-cart__quantity-subtotal"
									>
										<span style={headerNavBarDropMenuStickySubtotalSX}>
											{CartLabels.SubtotalLabel.t()}
										</span>{' '}
										{CartLabels.Subtotal.t({
											count,
										})}
									</Typography>
								</Stack>
								<Typography
									variant="bodyBaseline"
									sx={headerMiniCartLabelItemSX}
									className="quick-cart__price-subtotal"
								>
									{formatPrice(LOCALE_ES_MX, currency, totalPrice, decimalPlaces)}
								</Typography>
							</Stack>
							<Linkable
								onClick={onHandleClick}
								href={RouteLabels.Cart.route.t()}
								type="button"
								variant="contained"
								id="mini-cart-view-full-cart-button"
								data-testid="mini-cart-view-full-cart-button"
								sx={headerMiniCartButtonSX}
								disabled={isFetchingCart}
								className="quick-cart__cart-button"
							>
								{CartLabels.Actions.Cart.t()}
							</Linkable>
							<Typography variant="bodySubText" sx={headerMiniCartTermsTextSX}>
								{CartLabels.Consultation.t()}
								<Linkable
									href="terminos-condiciones"
									type="link"
									sx={headerMiniCartTermsLinkSX}
									target="_blank"
									rel="noopener noreferrer"
									className="quick-cart__tyc-link"
								>
									{CartLabels.TermsAndConditions.t()}
								</Linkable>
							</Typography>
						</Stack>
					</Stack>
				</Stack>
			) : (
				<Stack alignItems="center" sx={headerMiniCartContainerSX}>
					<Typography variant="bodySubText" align="center" sx={{ color: 'grey.500' }}>
						{CartLabels.Empty.t()}
					</Typography>
					<Img
						sx={headerMiniCartImageSX}
						src={CDN_BASE_URL + imageEmptyCartUrl}
						alt={CartLabels.EmptyCartImage.t()}
					/>
					<Linkable
						href="promociones"
						type="button"
						variant="outlined"
						id="mini-cart-discover-promotions-button"
						data-testid="mini-cart-discover-promotions-button"
						sx={headerMiniCartButtonSX}
						fullWidth
						className="quick-cart__browse-button"
					>
						{CartLabels.Actions.DiscoverBestPromotions.t()}
					</Linkable>
				</Stack>
			)}
		</Stack>
	);
};
